import React, { useMemo } from "react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import moment from "moment";
import { mergeSeriesByDate, computeKPI } from "./helpers";

const FIELD_MAP = {
  guestbook_views: "actions",
  property_views: "actions_unique",
  destination_views: "actions_unique",
  guest_checkins: "actions",
  extend_stay_messages: "actions",
  leads_generated: "actions",
  work_orders: "actions",
  text_threads: "actions",
  messages_sent: "actions",
  messages_received: "actions",
  ai_replies: "actions",
  fivestar_ratings: "actions",
  sent_sms: "actions",
  sent_email: "actions",
};

const axisProps = {
  x: {
    dataKey: "date",
    tick: { fontSize: 11, fill: "#94a3b8" },
    axisLine: { stroke: "#e2e8f0" },
    tickLine: false,
    interval: "preserveStartEnd",
  },
  y: {
    tick: { fontSize: 11, fill: "#94a3b8" },
    axisLine: false,
    tickLine: false,
    allowDecimals: false,
  },
  tooltip: {
    contentStyle: {
      backgroundColor: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: "8px",
      boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
      fontSize: "13px",
    },
  },
  grid: {
    strokeDasharray: "3 3",
    stroke: "#f0f0f0",
    vertical: false,
  },
};

const margin = { top: 5, right: 5, left: -20, bottom: 0 };

const MetricCard = React.memo(
  ({ title, data, dataKey, color, url, startDate, endDate, chartType = "area", series, overview, stacked }) => {
    // Multi-series mode (used for charts like Automated SMS & Email, Guestbook Views)
    const isMultiSeries = Array.isArray(series) && series.length > 0;

    const { chartData, total, trend, dailyAvg } = useMemo(() => {
      if (isMultiSeries) {
        // Merge multiple series from overview data
        const merged = mergeSeriesByDate(overview || {}, series, startDate, endDate);
        // Compute total from primary series (first one)
        const primaryKey = series[0].dataKey;
        const primaryData = (overview || {})[primaryKey] || [];
        const kpi = computeKPI(primaryData, series[0].field || "actions");
        return { chartData: merged, ...kpi };
      }

      // Single series mode
      const field = FIELD_MAP[dataKey] || "actions";
      const days =
        startDate && endDate
          ? Math.abs(
              moment(startDate, "YYYY-MM-DD")
                .startOf("day")
                .diff(moment(endDate, "YYYY-MM-DD").startOf("day"), "days"),
            ) + 1
          : 0;

      const arr = [];
      for (let i = 0; i < days; i++) {
        const day = moment(startDate).add(i, "days");
        const entry = (data || []).find(
          (d) => d.action_date === day.format("YYYY-MM-DD"),
        );
        arr.push({
          date: day.format("MMM D"),
          value: (entry || {})[field] || 0,
        });
      }

      const total = arr.reduce((sum, d) => sum + d.value, 0);
      const dailyAvg = days > 0 ? (total / days).toFixed(1) : 0;

      const mid = Math.floor(arr.length / 2);
      const firstHalf = arr.slice(0, mid).reduce((s, d) => s + d.value, 0);
      const secondHalf = arr.slice(mid).reduce((s, d) => s + d.value, 0);
      const trend =
        firstHalf > 0
          ? Math.round(((secondHalf - firstHalf) / firstHalf) * 100)
          : secondHalf > 0
            ? 100
            : 0;

      return { chartData: arr, total, trend, dailyAvg };
    }, [data, dataKey, startDate, endDate, isMultiSeries, series, overview]);

    const gradientId = `gradient-${dataKey || title.replace(/\s/g, "")}`;

    const renderChart = () => {
      switch (chartType) {
        case "bar":
          return (
            <BarChart data={chartData} margin={margin}>
              <CartesianGrid {...axisProps.grid} />
              <XAxis {...axisProps.x} />
              <YAxis {...axisProps.y} />
              <Tooltip {...axisProps.tooltip} />
              {isMultiSeries && <Legend />}
              {isMultiSeries ? (
                series.map(({ name, color: c }) => (
                  <Bar key={name} dataKey={name} fill={c} radius={[3, 3, 0, 0]} />
                ))
              ) : (
                <Bar dataKey="value" fill={color} radius={[3, 3, 0, 0]} />
              )}
            </BarChart>
          );

        case "line":
          return (
            <LineChart data={chartData} margin={margin}>
              <CartesianGrid {...axisProps.grid} />
              <XAxis {...axisProps.x} />
              <YAxis {...axisProps.y} />
              <Tooltip {...axisProps.tooltip} />
              {isMultiSeries && <Legend />}
              {isMultiSeries ? (
                series.map(({ name, color: c, dashed }) => (
                  <Line
                    key={name}
                    type="monotone"
                    dataKey={name}
                    stroke={c}
                    strokeWidth={2}
                    strokeDasharray={dashed ? "5 5" : undefined}
                    dot={false}
                    activeDot={{ r: 4, fill: c, stroke: "#fff", strokeWidth: 2 }}
                  />
                ))
              ) : (
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, fill: color, stroke: "#fff", strokeWidth: 2 }}
                />
              )}
            </LineChart>
          );

        case "area":
        default:
          return (
            <AreaChart data={chartData} margin={margin}>
              <defs>
                {isMultiSeries ? (
                  series.map(({ name, color: c }) => (
                    <linearGradient key={name} id={`grad-${name.replace(/\s/g, "")}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={c} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={c} stopOpacity={0.02} />
                    </linearGradient>
                  ))
                ) : (
                  <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={color} stopOpacity={0.02} />
                  </linearGradient>
                )}
              </defs>
              <CartesianGrid {...axisProps.grid} />
              <XAxis {...axisProps.x} />
              <YAxis {...axisProps.y} />
              <Tooltip {...axisProps.tooltip} />
              {isMultiSeries && <Legend />}
              {isMultiSeries ? (
                series.map(({ name, color: c }) => (
                  <Area
                    key={name}
                    type="monotone"
                    dataKey={name}
                    stroke={c}
                    strokeWidth={2}
                    fill={`url(#grad-${name.replace(/\s/g, "")})`}
                    stackId={stacked ? "stack" : undefined}
                    dot={false}
                    activeDot={{ r: 4, fill: c, stroke: "#fff", strokeWidth: 2 }}
                  />
                ))
              ) : (
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={color}
                  strokeWidth={2.5}
                  fill={`url(#${gradientId})`}
                  dot={false}
                  activeDot={{ r: 5, fill: color, stroke: "#fff", strokeWidth: 2 }}
                  animationDuration={800}
                />
              )}
            </AreaChart>
          );
      }
    };

    return (
      <div className="overview-metric-card">
        <div className="metric-header">
          <div className="metric-info">
            <h4 className="metric-title">{title}</h4>
            <div className="metric-total">
              <a href={"/reports/" + url}>{total.toLocaleString()}</a>
            </div>
            <div className="metric-details">
              <span className="metric-avg">{dailyAvg}/day avg</span>
              {trend !== 0 && (
                <span
                  className={`metric-trend ${trend > 0 ? "trend-up" : "trend-down"}`}
                >
                  {trend > 0 ? "↑" : "↓"} {Math.abs(trend)}%
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="metric-chart">
          {chartData && chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              {renderChart()}
            </ResponsiveContainer>
          ) : (
            <div className="metric-no-data">No data for this period</div>
          )}
        </div>
      </div>
    );
  },
);

MetricCard.displayName = "MetricCard";

export default MetricCard;
